import React from "react";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Check, CheckCheck } from "lucide-react";

export const MessageBubble = ({
  message,
  isOwn,
  senderName,
  timestamp,
  avatar,
  currentUserId, // Add this prop to determine message ownership
}) => {
  // Format message content
  const formatContent = (content) => {
    if (!content) return "";

    // Handle line breaks and ensure content is properly formatted
    return content.split("\n").map((line, index) => (
      <span key={index}>
        {line}
        {index < content.split("\n").length - 1 && <br />}
      </span>
    ));
  };

  // Get initials for avatar fallback
  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  // Determine message status based on the message data
  const getMessageStatus = () => {
    // Only show status for messages sent by current user
    if (!isOwn) return null;

    // If message has been seen by receiver
    if (message.seen) {
      return "read";
    }
    
    // If message has been delivered (exists in DB with updatedAt different from createdAt)
    if (message.updatedAt && message.createdAt && 
        new Date(message.updatedAt) > new Date(message.createdAt)) {
      return "delivered";
    }
    
    // If message exists but not seen and no update timestamp difference
    if (message._id || message.id) {
      return "sent";
    }
    
    // Default to sent if message exists
    return "sent";
  };

  const messageStatus = getMessageStatus();

  // Render status icon
  const renderStatusIcon = () => {
    if (!isOwn || !messageStatus) return null;

    switch (messageStatus) {
      case "sent":
        return <Check className="w-3 h-3 text-gray-400" />;
      case "delivered":
        return <CheckCheck className="w-3 h-3 text-gray-400" />;
      case "read":
        return <CheckCheck className="w-3 h-3 text-blue-500" />;
      default:
        return <Check className="w-3 h-3 text-gray-400" />;
    }
  };

  return (
    <div className={`flex ${isOwn ? "justify-end" : "justify-start"} group`}>
      <div
        className={`flex items-end space-x-2 max-w-[85%] md:max-w-[70%] ${
          isOwn ? "flex-row-reverse space-x-reverse" : ""
        }`}
      >
        {/* Avatar - only show for received messages */}
        {!isOwn && (
          <div className="flex-shrink-0">
            <Avatar className="w-6 h-6 md:w-8 md:h-8">
              <AvatarImage
                src={avatar}
                alt={senderName}
                className="w-full h-full object-cover"
              />
              <AvatarFallback className="bg-gradient-to-br from-gray-100 to-gray-200 text-gray-600 text-xs">
                {getInitials(senderName)}
              </AvatarFallback>
            </Avatar>
          </div>
        )}

        {/* Message Content */}
        <div className={`flex flex-col ${isOwn ? "items-end" : "items-start"}`}>
          {/* Message Bubble */}
          <div
            className={`px-3 py-2 md:px-4 md:py-2 rounded-2xl max-w-full word-wrap break-words ${
              isOwn
                ? "bg-igbanwo-mint text-white rounded-br-md"
                : "bg-gray-100 text-gray-900 rounded-bl-md"
            }`}
          >
            <p className="text-sm leading-relaxed">
              {formatContent(message.content || message.text)}
            </p>
          </div>

          {/* Message Info */}
          <div
            className={`flex items-center mt-1 space-x-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 ${
              isOwn ? "flex-row-reverse space-x-reverse" : ""
            }`}
          >
            {/* Timestamp */}
            <span className="text-xs text-gray-500">{timestamp}</span>

            {/* Message Status (for own messages only) */}
            {renderStatusIcon()}
          </div>
        </div>
      </div>
    </div>
  );
};